import {
  ApprovalRequestId,
  CommandActions,
  type CommandAction,
  isToolLifecycleItemType,
  type MessageId,
  type OrchestrationLatestTurn,
  type OrchestrationThreadActivity,
  type OrchestrationProposedPlanId,
  type ProviderKind,
  type ToolLifecycleItemType,
  type UserInputQuestion,
  type TurnId,
} from "@t3tools/contracts";
import { Schema } from "effect";

import type {
  ChatMessage,
  ProposedPlan,
  SessionPhase,
  ThreadSession,
  TurnDiffSummary,
} from "./types";

export type ProviderPickerKind = ProviderKind;

export const PROVIDER_OPTIONS: Array<{
  value: ProviderPickerKind;
  label: string;
  available: boolean;
}> = [{ value: "codex", label: "Codex", available: true }];

export interface WorkLogEntry {
  activityId?: OrchestrationThreadActivity["id"];
  payloadDeferred?: boolean;
  id: string;
  createdAt: string;
  turnId?: TurnId | null | undefined;
  label: string;
  detail?: string;
  command?: string;
  commandAction?: CommandAction;
  changedFiles?: ReadonlyArray<string>;
  tone: "thinking" | "tool" | "info" | "error";
  toolTitle?: string;
  /** A control-plane dynamic tool, such as `dashboard_generate`. */
  toolName?: string;
  toolStatus?: "running" | "completed" | "failed";
  itemType?: ToolLifecycleItemType;
  requestKind?: PendingApproval["requestKind"];
  userInput?: {
    status: "requested" | "answered";
    questions: ReadonlyArray<{ id: string; question: string; answer?: string }>;
  };
}

export interface PendingApproval {
  requestId: ApprovalRequestId;
  requestKind: "command" | "file-read" | "file-change";
  createdAt: string;
  detail?: string;
}

export interface PendingUserInput {
  requestId: ApprovalRequestId;
  createdAt: string;
  questions: ReadonlyArray<UserInputQuestion>;
}

export interface ActivePlanState {
  createdAt: string;
  turnId: TurnId | null;
  explanation?: string | null;
  steps: Array<{
    step: string;
    status: "pending" | "inProgress" | "completed";
  }>;
}

export interface LatestProposedPlanState {
  id: OrchestrationProposedPlanId;
  createdAt: string;
  updatedAt: string;
  turnId: TurnId | null;
  planMarkdown: string;
}

export type TimelineEntry =
  | {
      id: string;
      kind: "message";
      createdAt: string;
      message: ChatMessage;
    }
  | {
      id: string;
      kind: "proposed-plan";
      createdAt: string;
      proposedPlan: ProposedPlan;
    }
  | {
      id: string;
      kind: "work";
      createdAt: string;
      entry: WorkLogEntry;
    };

export interface TimelineActivePromptAnchor {
  turnId: TurnId;
  messageId: ChatMessage["id"];
  createdAt: string;
}

export interface TimelineAnchorOptions {
  activePromptAnchor?: TimelineActivePromptAnchor | null;
}

export type ChatMessageOrderOptions = TimelineAnchorOptions;

export interface CompletedTurnCollapseState {
  turnId: TurnId;
  finalEntryId: string;
  startedAt: string | null;
  completedAt: string | null;
  collapsedEntryIds: ReadonlySet<string>;
}

const DEFAULT_ACTIVE_PROMPT_ANCHOR_TOLERANCE_MS = 5000;

function chatMessageTimestampMs(message: Pick<ChatMessage, "createdAt">): number | null {
  const timestamp = Date.parse(message.createdAt);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function compareTimestampStrings(left: string, right: string): number {
  const leftMs = Date.parse(left);
  const rightMs = Date.parse(right);
  if (Number.isFinite(leftMs) && Number.isFinite(rightMs)) {
    return leftMs - rightMs;
  }

  return left.localeCompare(right);
}

export function shouldPlaceChatMessageBefore(
  candidate: ChatMessage,
  current: ChatMessage,
): boolean {
  if (candidate.role === "user" && current.role === "assistant" && current.streaming) {
    return true;
  }
  const candidateTimestamp = chatMessageTimestampMs(candidate);
  const currentTimestamp = chatMessageTimestampMs(current);
  if (candidateTimestamp === null || currentTimestamp === null) {
    return false;
  }
  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp < currentTimestamp;
  }
  return candidate.role === "user" && current.role === "assistant";
}

export function insertChatMessageForTimeline(
  messages: readonly ChatMessage[],
  nextMessage: ChatMessage,
): ChatMessage[] {
  const insertionIndex = messages.findIndex((message) =>
    shouldPlaceChatMessageBefore(nextMessage, message),
  );
  if (insertionIndex === -1) {
    return [...messages, nextMessage];
  }
  return [...messages.slice(0, insertionIndex), nextMessage, ...messages.slice(insertionIndex)];
}

interface SortableChatMessage {
  anchorUserMessageId: ChatMessage["id"] | null;
  effectiveCreatedAt: string;
  message: ChatMessage;
  originalIndex: number;
}

export function orderChatMessagesForTimeline(
  messages: readonly ChatMessage[],
  options: ChatMessageOrderOptions = {},
): ChatMessage[] {
  return messages
    .map((message, originalIndex) => buildSortableChatMessage(message, originalIndex, options))
    .toSorted(compareSortableChatMessagesForTimeline)
    .map((entry) => entry.message);
}

function compareSortableChatMessagesForTimeline(
  left: SortableChatMessage,
  right: SortableChatMessage,
): number {
  if (
    left.message.role === "user" &&
    right.message.role === "assistant" &&
    right.message.streaming
  ) {
    return -1;
  }

  if (
    right.message.role === "user" &&
    left.message.role === "assistant" &&
    left.message.streaming
  ) {
    return 1;
  }

  const promptAnchorOrder = compareChatMessagePromptAnchor(left, right);
  if (promptAnchorOrder !== 0) {
    return promptAnchorOrder;
  }

  const leftTimestamp = chatMessageTimestampMs({ createdAt: left.effectiveCreatedAt });
  const rightTimestamp = chatMessageTimestampMs({ createdAt: right.effectiveCreatedAt });
  if (leftTimestamp !== null && rightTimestamp !== null && leftTimestamp !== rightTimestamp) {
    return leftTimestamp - rightTimestamp;
  }

  if (leftTimestamp === null || rightTimestamp === null) {
    return left.originalIndex - right.originalIndex;
  }

  if (left.message.role === "user" && right.message.role === "assistant") {
    return -1;
  }

  if (right.message.role === "user" && left.message.role === "assistant") {
    return 1;
  }

  return left.originalIndex - right.originalIndex;
}

function buildSortableChatMessage(
  message: ChatMessage,
  originalIndex: number,
  options: ChatMessageOrderOptions,
): SortableChatMessage {
  const activePromptAnchor =
    message.role === "assistant" &&
    message.turnId &&
    options.activePromptAnchor?.turnId === message.turnId
      ? options.activePromptAnchor
      : null;
  return {
    anchorUserMessageId: activePromptAnchor?.messageId ?? null,
    effectiveCreatedAt: activePromptAnchor
      ? maxIsoTimestamp(message.createdAt, activePromptAnchor.createdAt)
      : message.createdAt,
    message,
    originalIndex,
  };
}

function compareChatMessagePromptAnchor(
  left: SortableChatMessage,
  right: SortableChatMessage,
): number {
  if (left.message.role === "user" && right.anchorUserMessageId === left.message.id) {
    return -1;
  }

  if (right.message.role === "user" && left.anchorUserMessageId === right.message.id) {
    return 1;
  }

  return 0;
}

export function deriveTimelineActivePromptAnchor(
  messages: readonly ChatMessage[],
  latestTurn: OrchestrationLatestTurn | null | undefined,
  toleranceMs = DEFAULT_ACTIVE_PROMPT_ANCHOR_TOLERANCE_MS,
): TimelineActivePromptAnchor | null {
  if (!latestTurn) {
    return null;
  }

  const requestedAtMs = Date.parse(latestTurn.requestedAt);
  if (!Number.isFinite(requestedAtMs)) {
    return null;
  }

  let latestPromptMs = Number.POSITIVE_INFINITY;
  if (latestTurn.state !== "running") {
    if (!latestTurn.completedAt) {
      return null;
    }
    latestPromptMs = Date.parse(latestTurn.completedAt);
    if (!Number.isFinite(latestPromptMs)) {
      return null;
    }
  }

  const earliestPromptMs = requestedAtMs - toleranceMs;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message || message.role !== "user") {
      continue;
    }
    const messageCreatedAtMs = chatMessageTimestampMs(message);
    if (messageCreatedAtMs === null) {
      continue;
    }
    if (messageCreatedAtMs < earliestPromptMs || messageCreatedAtMs > latestPromptMs) {
      continue;
    }
    return {
      turnId: latestTurn.turnId,
      messageId: message.id,
      createdAt: message.createdAt,
    };
  }

  return null;
}

export function formatDuration(durationMs: number): string {
  if (!Number.isFinite(durationMs) || durationMs < 0) return "0ms";
  if (durationMs < 1_000) return `${Math.max(1, Math.round(durationMs))}ms`;
  if (durationMs < 10_000) return `${(durationMs / 1_000).toFixed(1)}s`;
  if (durationMs < 60_000) return `${Math.round(durationMs / 1_000)}s`;
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.round((durationMs % 60_000) / 1_000);
  if (seconds === 0) return `${minutes}m`;
  if (seconds === 60) return `${minutes + 1}m`;
  return `${minutes}m ${seconds}s`;
}

export function formatElapsed(startIso: string, endIso: string | undefined): string | null {
  if (!endIso) return null;
  const startedAt = Date.parse(startIso);
  const endedAt = Date.parse(endIso);
  if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt < startedAt) {
    return null;
  }
  return formatDuration(endedAt - startedAt);
}

type LatestTurnTiming = Pick<OrchestrationLatestTurn, "turnId" | "startedAt" | "completedAt">;
type SessionActivityState = Pick<ThreadSession, "orchestrationStatus" | "activeTurnId">;

export function isLatestTurnSettled(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
): boolean {
  if (!latestTurn?.startedAt) return false;
  if (!latestTurn.completedAt) return false;
  if (!session) return true;
  if (session.orchestrationStatus === "running") return false;
  return true;
}

export function deriveActiveWorkStartedAt(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
  sendStartedAt: string | null,
): string | null {
  if (!isLatestTurnSettled(latestTurn, session)) {
    return latestTurn?.startedAt ?? sendStartedAt;
  }
  return sendStartedAt;
}

function requestKindFromRequestType(requestType: unknown): PendingApproval["requestKind"] | null {
  switch (requestType) {
    case "command_execution_approval":
    case "exec_command_approval":
      return "command";
    case "file_read_approval":
      return "file-read";
    case "file_change_approval":
    case "apply_patch_approval":
      return "file-change";
    default:
      return null;
  }
}

export function derivePendingApprovals(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): PendingApproval[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingApproval>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.makeUnsafe(payload.requestId)
        : null;
    const requestKind =
      payload &&
      (payload.requestKind === "command" ||
        payload.requestKind === "file-read" ||
        payload.requestKind === "file-change")
        ? payload.requestKind
        : payload
          ? requestKindFromRequestType(payload.requestType)
          : null;
    const detail = payload && typeof payload.detail === "string" ? payload.detail : undefined;

    if (activity.kind === "approval.requested" && requestId && requestKind) {
      openByRequestId.set(requestId, {
        requestId,
        requestKind,
        createdAt: activity.createdAt,
        ...(detail ? { detail } : {}),
      });
      continue;
    }

    if (activity.kind === "approval.resolved" && requestId) {
      openByRequestId.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.approval.respond.failed" &&
      requestId &&
      detail?.includes("Unknown pending permission request")
    ) {
      openByRequestId.delete(requestId);
      continue;
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    compareTimestampStrings(left.createdAt, right.createdAt),
  );
}

function parseUserInputQuestions(
  payload: Record<string, unknown> | null,
): ReadonlyArray<UserInputQuestion> | null {
  const questions = payload?.questions;
  if (!Array.isArray(questions)) {
    return null;
  }
  const parsed = questions
    .map<UserInputQuestion | null>((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const question = entry as Record<string, unknown>;
      if (
        typeof question.id !== "string" ||
        typeof question.header !== "string" ||
        typeof question.question !== "string" ||
        !Array.isArray(question.options)
      ) {
        return null;
      }
      const options = question.options
        .map<UserInputQuestion["options"][number] | null>((option) => {
          if (!option || typeof option !== "object") return null;
          const optionRecord = option as Record<string, unknown>;
          if (
            typeof optionRecord.label !== "string" ||
            typeof optionRecord.description !== "string"
          ) {
            return null;
          }
          return {
            label: optionRecord.label,
            description: optionRecord.description,
          };
        })
        .filter((option): option is UserInputQuestion["options"][number] => option !== null);
      if (options.length === 0) {
        return null;
      }
      return {
        id: question.id,
        header: question.header,
        question: question.question,
        options,
      };
    })
    .filter((question): question is UserInputQuestion => question !== null);
  return parsed.length > 0 ? parsed : null;
}

export function derivePendingUserInputs(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): PendingUserInput[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingUserInput>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.makeUnsafe(payload.requestId)
        : null;

    if (activity.kind === "user-input.requested" && requestId) {
      const questions = parseUserInputQuestions(payload);
      if (!questions) {
        continue;
      }
      openByRequestId.set(requestId, {
        requestId,
        createdAt: activity.createdAt,
        questions,
      });
      continue;
    }

    if (activity.kind === "user-input.resolved" && requestId) {
      openByRequestId.delete(requestId);
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    compareTimestampStrings(left.createdAt, right.createdAt),
  );
}

export function deriveActivePlanState(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): ActivePlanState | null {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const candidates = ordered.filter((activity) => {
    if (activity.kind !== "turn.plan.updated") {
      return false;
    }
    if (!latestTurnId) {
      return true;
    }
    return activity.turnId === latestTurnId;
  });
  const latest = candidates.at(-1);
  if (!latest) {
    return null;
  }
  const payload = asRecord(latest.payload);
  const steps = parsePlanSteps(payload);
  if (steps.length === 0) {
    return null;
  }
  return {
    createdAt: latest.createdAt,
    turnId: latest.turnId,
    ...(payload && "explanation" in payload
      ? { explanation: payload.explanation as string | null }
      : {}),
    steps,
  };
}

function parsePlanSteps(
  payload: Record<string, unknown> | null,
): Array<{ step: string; status: "pending" | "inProgress" | "completed" }> {
  const rawPlan = payload?.plan;
  if (!Array.isArray(rawPlan)) {
    return [];
  }

  return rawPlan
    .map((entry) => {
      const record = asRecord(entry);
      if (!record || typeof record.step !== "string") {
        return null;
      }
      const status =
        record.status === "completed" || record.status === "inProgress" ? record.status : "pending";
      return {
        step: record.step,
        status,
      };
    })
    .filter(
      (
        step,
      ): step is {
        step: string;
        status: "pending" | "inProgress" | "completed";
      } => step !== null,
    );
}

export function buildPlanMarkdownFromPlanUpdatePayload(payload: unknown): string | null {
  const payloadRecord = asRecord(payload);
  const steps = parsePlanSteps(payloadRecord);
  if (steps.length === 0) {
    return null;
  }

  const lines: string[] = [];
  const explanation = asTrimmedString(payloadRecord?.explanation);
  if (explanation) {
    lines.push(explanation, "");
  }

  for (const step of steps) {
    const checkbox =
      step.status === "completed" ? "[x]" : step.status === "inProgress" ? "[-]" : "[ ]";
    lines.push(`- ${checkbox} ${step.step}`);
  }

  return lines.join("\n");
}

export function findLatestProposedPlan(
  proposedPlans: ReadonlyArray<ProposedPlan>,
  latestTurnId: TurnId | string | null | undefined,
): LatestProposedPlanState | null {
  if (latestTurnId) {
    const matchingTurnPlan = [...proposedPlans]
      .filter((proposedPlan) => proposedPlan.turnId === latestTurnId)
      .toSorted(
        (left, right) =>
          compareTimestampStrings(left.updatedAt, right.updatedAt) ||
          left.id.localeCompare(right.id),
      )
      .at(-1);
    if (matchingTurnPlan) {
      return {
        id: matchingTurnPlan.id,
        createdAt: matchingTurnPlan.createdAt,
        updatedAt: matchingTurnPlan.updatedAt,
        turnId: matchingTurnPlan.turnId,
        planMarkdown: matchingTurnPlan.planMarkdown,
      };
    }
  }

  const latestPlan = [...proposedPlans]
    .toSorted(
      (left, right) =>
        compareTimestampStrings(left.updatedAt, right.updatedAt) || left.id.localeCompare(right.id),
    )
    .at(-1);
  if (!latestPlan) {
    return null;
  }

  return {
    id: latestPlan.id,
    createdAt: latestPlan.createdAt,
    updatedAt: latestPlan.updatedAt,
    turnId: latestPlan.turnId,
    planMarkdown: latestPlan.planMarkdown,
  };
}

export function deriveWorkLogEntries(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): WorkLogEntry[] {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const questionsByRequestId = new Map<string, ReadonlyArray<UserInputQuestion>>();
  const answeredRequestIds = new Set<string>();
  const completedToolCallIds = new Set<string>();
  for (const activity of ordered) {
    const payload = asRecord(activity.payload);
    if (activity.kind === "tool.completed" && typeof payload?.callId === "string") {
      completedToolCallIds.add(payload.callId);
    }
    if (activity.kind === "user-input.requested" && typeof payload?.requestId === "string") {
      questionsByRequestId.set(payload.requestId, parseUserInputQuestions(payload) ?? []);
    }
    if (
      activity.kind === "user-input.resolved" &&
      typeof payload?.requestId === "string" &&
      (!latestTurnId || activity.turnId === latestTurnId)
    ) {
      answeredRequestIds.add(payload.requestId);
    }
  }
  return (
    ordered
      .filter((activity) => (latestTurnId ? activity.turnId === latestTurnId : true))
      .filter((activity) => activity.kind !== "tool.started")
      .filter((activity) => activity.kind !== "task.started" && activity.kind !== "task.completed")
      .filter((activity) => activity.summary !== "Checkpoint captured")
      // A finished dynamic tool replaces its in-progress row.
      .filter(
        (activity) =>
          activity.kind !== "tool.called" ||
          !completedToolCallIds.has(String(asRecord(activity.payload)?.callId)),
      )
      // The answered entry includes the original question, so keep one disclosure per exchange.
      .filter(
        (activity) =>
          activity.kind !== "user-input.requested" ||
          !answeredRequestIds.has(String(asRecord(activity.payload)?.requestId)),
      )
      .flatMap((activity): WorkLogEntry[] => {
        const payload =
          activity.payload && typeof activity.payload === "object"
            ? (activity.payload as Record<string, unknown>)
            : null;
        const command = extractToolCommand(payload);
        const changedFiles = extractChangedFiles(payload);
        const title = extractToolTitle(payload);
        const entry: WorkLogEntry = {
          id: activity.id,
          activityId: activity.id,
          ...(activity.payloadDeferred ? { payloadDeferred: true } : {}),
          createdAt: activity.createdAt,
          turnId: activity.turnId,
          label: activity.summary,
          tone: activity.tone === "approval" ? "info" : activity.tone,
        };
        if (activity.kind === "user-input.requested" || activity.kind === "user-input.resolved") {
          const answered = activity.kind === "user-input.resolved";
          const questions = questionsByRequestId.get(String(payload?.requestId)) ?? [];
          const answers = asRecord(payload?.answers) ?? {};
          const items = questions.map((question) => ({
            id: question.id,
            question: question.question,
            ...(answered ? { answer: formatUserInputAnswer(answers[question.id]) } : {}),
          }));
          // Preserve answers even if an older request is absent from the loaded history.
          for (const [id, answer] of Object.entries(answers)) {
            if (!questions.some((question) => question.id === id)) {
              items.push({ id, question: id, answer: formatUserInputAnswer(answer) });
            }
          }
          if (items.length > 0)
            entry.userInput = { status: answered ? "answered" : "requested", questions: items };
        }
        const itemType = extractWorkLogItemType(payload);
        const requestKind = extractWorkLogRequestKind(payload);
        if (payload && typeof payload.detail === "string" && payload.detail.length > 0) {
          const detail = stripTrailingExitCode(payload.detail).output;
          if (detail) {
            entry.detail = detail;
          }
        }
        if (command) {
          entry.command = command;
        }
        if (changedFiles.length > 0) {
          entry.changedFiles = changedFiles;
        }
        if (title) {
          entry.toolTitle = title;
        }
        const toolName = asTrimmedString(payload?.toolName);
        if (toolName && toolName !== "unknown") {
          entry.toolName = toolName;
          if (activity.kind === "tool.called") entry.toolStatus = "running";
          if (activity.kind === "tool.completed")
            entry.toolStatus = payload?.success === false ? "failed" : "completed";
        }
        if (itemType) {
          entry.itemType = itemType;
        }
        if (requestKind) {
          entry.requestKind = requestKind;
        }
        const data = asRecord(payload?.data);
        const item = asRecord(data?.item);
        const commandActions =
          payload?.commandActions ?? item?.commandActions ?? data?.commandActions;
        const status = payload?.status ?? item?.status ?? data?.status;
        const exitCode = payload?.exitCode ?? item?.exitCode ?? data?.exitCode;
        const success = payload?.success ?? item?.success ?? data?.success;
        // Unknown or unsuccessful executions retain the ordinary command display.
        // Keep a mixed command's unknown actions so reads never hide other work.
        if (
          command &&
          !requestKind &&
          entry.tone !== "error" &&
          status !== "failed" &&
          status !== "declined" &&
          success !== false &&
          !(typeof exitCode === "number" && exitCode !== 0) &&
          isCommandActions(commandActions) &&
          commandActions.some((action) => action.type !== "unknown")
        ) {
          return commandActions.map((commandAction, index) => ({
            ...entry,
            id: `${entry.id}:action:${index}`,
            commandAction,
          }));
        }
        return [entry];
      })
  );
}

const isCommandActions = Schema.is(CommandActions);

function formatUserInputAnswer(value: unknown): string {
  if (typeof value === "string") return value;
  const record = asRecord(value);
  const values = Array.isArray(value)
    ? value
    : Array.isArray(record?.answers)
      ? record.answers
      : [record?.selectedOptionLabel, record?.customAnswer];
  return (
    values
      .filter((answer): answer is string => typeof answer === "string" && answer.length > 0)
      .join("\n") || "Answer unavailable"
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeCommandValue(value: unknown): string | null {
  const direct = asTrimmedString(value);
  if (direct) {
    return direct;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  const parts = value
    .map((entry) => asTrimmedString(entry))
    .filter((entry): entry is string => entry !== null);
  return parts.length > 0 ? parts.join(" ") : null;
}

function extractToolCommand(payload: Record<string, unknown> | null): string | null {
  const data = asRecord(payload?.data);
  const item = asRecord(data?.item);
  const itemResult = asRecord(item?.result);
  const itemInput = asRecord(item?.input);
  const candidates = [
    normalizeCommandValue(payload?.command),
    normalizeCommandValue(item?.command),
    normalizeCommandValue(itemInput?.command),
    normalizeCommandValue(itemResult?.command),
    normalizeCommandValue(data?.command),
  ];
  return candidates.find((candidate) => candidate !== null) ?? null;
}

function extractToolTitle(payload: Record<string, unknown> | null): string | null {
  return asTrimmedString(payload?.title);
}

function stripTrailingExitCode(value: string): {
  output: string | null;
  exitCode?: number | undefined;
} {
  const trimmed = value.trim();
  const match = /^(?<output>[\s\S]*?)(?:\s*<exited with exit code (?<code>\d+)>)\s*$/i.exec(
    trimmed,
  );
  if (!match?.groups) {
    return {
      output: trimmed.length > 0 ? trimmed : null,
    };
  }
  const exitCode = Number.parseInt(match.groups.code ?? "", 10);
  const normalizedOutput = match.groups.output?.trim() ?? "";
  return {
    output: normalizedOutput.length > 0 ? normalizedOutput : null,
    ...(Number.isInteger(exitCode) ? { exitCode } : {}),
  };
}

function extractWorkLogItemType(
  payload: Record<string, unknown> | null,
): WorkLogEntry["itemType"] | undefined {
  if (typeof payload?.itemType === "string" && isToolLifecycleItemType(payload.itemType)) {
    return payload.itemType;
  }
  return undefined;
}

function extractWorkLogRequestKind(
  payload: Record<string, unknown> | null,
): WorkLogEntry["requestKind"] | undefined {
  if (
    payload?.requestKind === "command" ||
    payload?.requestKind === "file-read" ||
    payload?.requestKind === "file-change"
  ) {
    return payload.requestKind;
  }
  return requestKindFromRequestType(payload?.requestType) ?? undefined;
}

function pushChangedFile(target: string[], seen: Set<string>, value: unknown) {
  const normalized = asTrimmedString(value);
  if (!normalized || seen.has(normalized)) {
    return;
  }
  seen.add(normalized);
  target.push(normalized);
}

function collectChangedFiles(value: unknown, target: string[], seen: Set<string>, depth: number) {
  if (depth > 4 || target.length >= 12) {
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      collectChangedFiles(entry, target, seen, depth + 1);
      if (target.length >= 12) {
        return;
      }
    }
    return;
  }

  const record = asRecord(value);
  if (!record) {
    return;
  }

  pushChangedFile(target, seen, record.path);
  pushChangedFile(target, seen, record.filePath);
  pushChangedFile(target, seen, record.relativePath);
  pushChangedFile(target, seen, record.filename);
  pushChangedFile(target, seen, record.newPath);
  pushChangedFile(target, seen, record.oldPath);

  for (const nestedKey of [
    "item",
    "result",
    "input",
    "data",
    "changes",
    "files",
    "edits",
    "patch",
    "patches",
    "operations",
  ]) {
    if (!(nestedKey in record)) {
      continue;
    }
    collectChangedFiles(record[nestedKey], target, seen, depth + 1);
    if (target.length >= 12) {
      return;
    }
  }
}

function extractChangedFiles(payload: Record<string, unknown> | null): string[] {
  const changedFiles: string[] = [];
  const seen = new Set<string>();
  collectChangedFiles(asRecord(payload?.data), changedFiles, seen, 0);
  return changedFiles;
}

function compareActivitiesByOrder(
  left: OrchestrationThreadActivity,
  right: OrchestrationThreadActivity,
): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  }

  return (
    compareTimestampStrings(left.createdAt, right.createdAt) || left.id.localeCompare(right.id)
  );
}

export function hasToolActivityForTurn(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  turnId: TurnId | null | undefined,
): boolean {
  if (!turnId) return false;
  return activities.some((activity) => activity.turnId === turnId && activity.tone === "tool");
}

export function deriveTimelineEntries(
  messages: ChatMessage[],
  proposedPlans: ProposedPlan[],
  workEntries: WorkLogEntry[],
  options: TimelineAnchorOptions = {},
): TimelineEntry[] {
  const messageRows: TimelineEntry[] = messages.map((message) => ({
    id: message.id,
    kind: "message",
    createdAt: message.createdAt,
    message,
  }));
  const proposedPlanRows: TimelineEntry[] = proposedPlans.map((proposedPlan) => ({
    id: proposedPlan.id,
    kind: "proposed-plan",
    createdAt: proposedPlan.createdAt,
    proposedPlan,
  }));
  const workRows: TimelineEntry[] = workEntries.map((entry) => ({
    id: entry.id,
    kind: "work",
    createdAt: entry.createdAt,
    entry,
  }));
  const timelineAnchors = buildTimelineAnchors(messages, options);
  return [...messageRows, ...proposedPlanRows, ...workRows]
    .map((entry, originalIndex) =>
      buildSortableTimelineEntry(entry, originalIndex, timelineAnchors),
    )
    .toSorted(compareSortableTimelineEntries)
    .map((entry) => entry.timelineEntry);
}

export function deriveCompletedTurnCollapseState(
  timelineEntries: readonly TimelineEntry[],
  latestTurn: OrchestrationLatestTurn | null | undefined,
  latestTurnSettled: boolean,
): CompletedTurnCollapseState | null {
  if (!latestTurnSettled || !latestTurn?.turnId) {
    return null;
  }
  if (latestTurn.state !== "completed") {
    return null;
  }

  const finalEntryIndex = findFinalAssistantEntryIndex(timelineEntries, latestTurn);
  if (finalEntryIndex < 0) {
    return null;
  }

  const finalEntry = timelineEntries[finalEntryIndex];
  if (!finalEntry) {
    return null;
  }

  const collapsedEntryIds = new Set<string>();
  for (let index = 0; index < timelineEntries.length; index += 1) {
    if (index === finalEntryIndex) {
      continue;
    }
    const entry = timelineEntries[index];
    if (!entry || timelineEntryTurnId(entry) !== latestTurn.turnId) {
      continue;
    }
    if (entry.kind === "message" && entry.message.role !== "assistant") {
      continue;
    }
    if (entry.kind === "work" && entry.entry.userInput) continue;
    collapsedEntryIds.add(entry.id);
  }

  return {
    turnId: latestTurn.turnId,
    finalEntryId: finalEntry.id,
    ...deriveCompletedTurnTiming(timelineEntries, latestTurn.turnId, finalEntryIndex, latestTurn),
    collapsedEntryIds,
  };
}

export function deriveCompletedTurnCollapseStates(
  timelineEntries: readonly TimelineEntry[],
  latestTurn: OrchestrationLatestTurn | null | undefined,
  latestTurnSettled: boolean,
  options: {
    completedTurnIds?: ReadonlySet<TurnId> | undefined;
    excludedTurnIds?: ReadonlySet<TurnId> | undefined;
    finalAssistantMessageIdByTurnId?: ReadonlyMap<TurnId, MessageId> | undefined;
  } = {},
): CompletedTurnCollapseState[] {
  const excludedLatestTurnId =
    latestTurn?.turnId && latestTurn.state !== "completed" ? latestTurn.turnId : null;
  const finalEntryIndexByTurnId = new Map<TurnId, number>();

  for (let index = 0; index < timelineEntries.length; index += 1) {
    const entry = timelineEntries[index];
    if (
      entry?.kind !== "message" ||
      entry.message.role !== "assistant" ||
      !entry.message.turnId ||
      entry.message.streaming
    ) {
      continue;
    }
    const existingIndex = finalEntryIndexByTurnId.get(entry.message.turnId);
    if (
      existingIndex === undefined ||
      compareAssistantFinalEntryCandidates(entry, timelineEntries[existingIndex]) >= 0
    ) {
      finalEntryIndexByTurnId.set(entry.message.turnId, index);
    }
  }

  for (const [turnId, messageId] of options.finalAssistantMessageIdByTurnId ?? []) {
    const preferredIndex = timelineEntries.findIndex(
      (entry) =>
        entry.kind === "message" &&
        entry.message.role === "assistant" &&
        entry.message.turnId === turnId &&
        entry.message.id === messageId,
    );
    if (preferredIndex >= 0) {
      finalEntryIndexByTurnId.set(turnId, preferredIndex);
    }
  }

  if (latestTurnSettled && latestTurn?.state === "completed") {
    const latestFinalEntryIndex = findFinalAssistantEntryIndex(timelineEntries, latestTurn);
    if (latestFinalEntryIndex >= 0) {
      finalEntryIndexByTurnId.set(latestTurn.turnId, latestFinalEntryIndex);
    }
  }

  return [...finalEntryIndexByTurnId.entries()]
    .toSorted((left, right) => left[1] - right[1])
    .flatMap(([turnId, finalEntryIndex]) => {
      if (excludedLatestTurnId === turnId) {
        return [];
      }
      if (options.excludedTurnIds?.has(turnId)) {
        return [];
      }
      if (options.completedTurnIds && !options.completedTurnIds.has(turnId)) {
        return [];
      }
      const state = buildCompletedTurnCollapseState(
        timelineEntries,
        turnId,
        finalEntryIndex,
        latestTurn?.turnId === turnId ? latestTurn : null,
      );
      return state ? [state] : [];
    });
}

function buildCompletedTurnCollapseState(
  timelineEntries: readonly TimelineEntry[],
  turnId: TurnId,
  finalEntryIndex: number,
  latestTurn: OrchestrationLatestTurn | null,
): CompletedTurnCollapseState | null {
  const finalEntry = timelineEntries[finalEntryIndex];
  if (!finalEntry) {
    return null;
  }

  const collapsedEntryIds = new Set<string>();
  for (let index = 0; index < timelineEntries.length; index += 1) {
    if (index === finalEntryIndex) {
      continue;
    }
    const entry = timelineEntries[index];
    if (!entry || timelineEntryTurnId(entry) !== turnId) {
      continue;
    }
    if (entry.kind === "message" && entry.message.role !== "assistant") {
      continue;
    }
    if (entry.kind === "work" && entry.entry.userInput) continue;
    collapsedEntryIds.add(entry.id);
  }

  return {
    turnId,
    finalEntryId: finalEntry.id,
    ...deriveCompletedTurnTiming(timelineEntries, turnId, finalEntryIndex, latestTurn),
    collapsedEntryIds,
  };
}

function compareAssistantFinalEntryCandidates(
  left: TimelineEntry,
  right: TimelineEntry | undefined,
): number {
  if (!right || left.kind !== "message" || right.kind !== "message") {
    return 0;
  }
  return (
    compareTimestampStrings(
      left.message.completedAt ?? left.message.createdAt,
      right.message.completedAt ?? right.message.createdAt,
    ) || compareTimestampStrings(left.createdAt, right.createdAt)
  );
}

function deriveCompletedTurnTiming(
  timelineEntries: readonly TimelineEntry[],
  turnId: TurnId,
  finalEntryIndex: number,
  latestTurn: OrchestrationLatestTurn | null,
): Pick<CompletedTurnCollapseState, "startedAt" | "completedAt"> {
  const finalEntry = timelineEntries[finalEntryIndex];
  const finalMessage = finalEntry?.kind === "message" ? finalEntry.message : null;
  let firstSameTurnAssistantIndex = -1;
  for (let index = 0; index <= finalEntryIndex; index += 1) {
    const entry = timelineEntries[index];
    if (
      entry?.kind === "message" &&
      entry.message.role === "assistant" &&
      entry.message.turnId === turnId
    ) {
      firstSameTurnAssistantIndex = index;
      break;
    }
  }

  const firstSameTurnAssistant =
    firstSameTurnAssistantIndex >= 0 ? timelineEntries[firstSameTurnAssistantIndex] : null;
  let firstSameTurnContent: TimelineEntry | null = null;
  for (let index = 0; index <= finalEntryIndex; index += 1) {
    const entry = timelineEntries[index];
    if (!entry || timelineEntryTurnId(entry) !== turnId) {
      continue;
    }
    if (entry.kind === "message" && entry.message.role === "user") {
      continue;
    }
    firstSameTurnContent = entry;
    break;
  }
  let promptEntry: TimelineEntry | null = null;
  if (firstSameTurnAssistantIndex >= 0) {
    for (let index = firstSameTurnAssistantIndex - 1; index >= 0; index -= 1) {
      const entry = timelineEntries[index];
      if (entry?.kind === "message" && entry.message.role === "user") {
        promptEntry = entry;
        break;
      }
    }
  }

  const requestedAt =
    latestTurn?.requestedAt ??
    promptEntry?.createdAt ??
    firstSameTurnAssistant?.createdAt ??
    finalEntry?.createdAt ??
    null;
  const firstAssistantStartedAt =
    firstSameTurnAssistant?.kind === "message" ? firstSameTurnAssistant.message.createdAt : null;
  const startedAt =
    latestTurn?.startedAt ??
    firstSameTurnContent?.createdAt ??
    firstAssistantStartedAt ??
    requestedAt;
  const completedAt =
    latestTurn?.completedAt ?? finalMessage?.completedAt ?? finalEntry?.createdAt ?? null;

  return { startedAt, completedAt };
}

function findFinalAssistantEntryIndex(
  timelineEntries: readonly TimelineEntry[],
  latestTurn: OrchestrationLatestTurn,
): number {
  if (latestTurn.assistantMessageId) {
    const finalEntryIndex = timelineEntries.findIndex(
      (entry) =>
        entry.kind === "message" &&
        entry.message.id === latestTurn.assistantMessageId &&
        entry.message.role === "assistant" &&
        entry.message.turnId === latestTurn.turnId,
    );
    if (finalEntryIndex >= 0) {
      return finalEntryIndex;
    }
  }

  for (let index = timelineEntries.length - 1; index >= 0; index -= 1) {
    const entry = timelineEntries[index];
    if (
      entry?.kind === "message" &&
      entry.message.role === "assistant" &&
      entry.message.turnId === latestTurn.turnId &&
      !entry.message.streaming
    ) {
      return index;
    }
  }

  return -1;
}

export function findFirstTimelineEntryIdForTurnContent(
  timelineEntries: readonly TimelineEntry[],
  turnId: TurnId | null | undefined,
): string | null {
  if (!turnId) {
    return null;
  }

  for (const entry of timelineEntries) {
    if (timelineEntryTurnId(entry) !== turnId) {
      continue;
    }
    if (entry.kind === "message" && entry.message.role === "user") {
      continue;
    }
    return entry.id;
  }

  return null;
}

interface TurnAnchor {
  createdAt: string;
  messageId: ChatMessage["id"];
}

interface SortableTimelineEntry {
  anchorUserMessageId: ChatMessage["id"] | null;
  effectiveCreatedAt: string;
  originalIndex: number;
  timelineEntry: TimelineEntry;
}

interface TimelineAnchors {
  messageAnchors: ReadonlyMap<string, TurnAnchor>;
  turnAnchors: ReadonlyMap<TurnId, TurnAnchor>;
}

function buildTimelineAnchors(
  messages: ChatMessage[],
  options: TimelineAnchorOptions,
): TimelineAnchors {
  const messageAnchors = new Map<string, TurnAnchor>();
  const turnAnchors = new Map<TurnId, TurnAnchor>();
  let latestUserAnchor: TurnAnchor | null = null;

  for (const message of messages) {
    if (message.role === "user") {
      latestUserAnchor = {
        createdAt: message.createdAt,
        messageId: message.id,
      };
      continue;
    }

    if (message.role === "assistant" && latestUserAnchor) {
      if (message.turnId) {
        turnAnchors.set(message.turnId, latestUserAnchor);
      }
      if (message.streaming) {
        messageAnchors.set(message.id, latestUserAnchor);
      }
    }
  }

  const activePromptAnchor = options.activePromptAnchor;
  if (activePromptAnchor) {
    const existingAnchor = turnAnchors.get(activePromptAnchor.turnId);
    if (
      !existingAnchor ||
      isTimestampAtOrAfter(activePromptAnchor.createdAt, existingAnchor.createdAt)
    ) {
      turnAnchors.set(activePromptAnchor.turnId, {
        createdAt: activePromptAnchor.createdAt,
        messageId: activePromptAnchor.messageId,
      });
    }
  }

  return { messageAnchors, turnAnchors };
}

export function timelineEntryTurnId(entry: TimelineEntry): TurnId | null | undefined {
  switch (entry.kind) {
    case "message":
      return entry.message.turnId;
    case "proposed-plan":
      return entry.proposedPlan.turnId;
    case "work":
      return entry.entry.turnId;
  }
}

function maxIsoTimestamp(left: string, right: string): string {
  const leftMs = Date.parse(left);
  const rightMs = Date.parse(right);
  if (Number.isFinite(leftMs) && Number.isFinite(rightMs)) {
    return rightMs > leftMs ? right : left;
  }

  return right > left ? right : left;
}

function isTimestampAtOrAfter(left: string, right: string): boolean {
  const leftMs = Date.parse(left);
  const rightMs = Date.parse(right);
  if (Number.isFinite(leftMs) && Number.isFinite(rightMs)) {
    return leftMs >= rightMs;
  }

  return left >= right;
}

function buildSortableTimelineEntry(
  timelineEntry: TimelineEntry,
  originalIndex: number,
  timelineAnchors: TimelineAnchors,
): SortableTimelineEntry {
  const turnId = timelineEntryTurnId(timelineEntry);
  const turnAnchor = turnId ? (timelineAnchors.turnAnchors.get(turnId) ?? null) : null;
  const messageAnchor =
    timelineEntry.kind === "message"
      ? (timelineAnchors.messageAnchors.get(timelineEntry.message.id) ?? null)
      : null;
  const anchor = turnAnchor ?? messageAnchor;
  return {
    anchorUserMessageId: anchor?.messageId ?? null,
    effectiveCreatedAt: anchor
      ? maxIsoTimestamp(timelineEntry.createdAt, anchor.createdAt)
      : timelineEntry.createdAt,
    originalIndex,
    timelineEntry,
  };
}

function comparePromptAnchor(left: SortableTimelineEntry, right: SortableTimelineEntry): number {
  if (
    left.timelineEntry.kind === "message" &&
    left.timelineEntry.message.role === "user" &&
    right.anchorUserMessageId === left.timelineEntry.message.id
  ) {
    return -1;
  }

  if (
    right.timelineEntry.kind === "message" &&
    right.timelineEntry.message.role === "user" &&
    left.anchorUserMessageId === right.timelineEntry.message.id
  ) {
    return 1;
  }

  return 0;
}

function compareSortableTimelineEntries(
  left: SortableTimelineEntry,
  right: SortableTimelineEntry,
): number {
  const promptAnchorOrder = comparePromptAnchor(left, right);
  if (promptAnchorOrder !== 0) {
    return promptAnchorOrder;
  }

  const createdAtOrder = compareTimestampStrings(left.effectiveCreatedAt, right.effectiveCreatedAt);
  if (createdAtOrder !== 0) {
    return createdAtOrder;
  }

  return left.originalIndex - right.originalIndex;
}

export function inferCheckpointTurnCountByTurnId(
  summaries: TurnDiffSummary[],
): Record<TurnId, number> {
  const sorted = [...summaries].toSorted((a, b) =>
    compareTimestampStrings(a.completedAt, b.completedAt),
  );
  const result: Record<TurnId, number> = {};
  for (let index = 0; index < sorted.length; index += 1) {
    const summary = sorted[index];
    if (!summary) continue;
    result[summary.turnId] = index + 1;
  }
  return result;
}

export function derivePhase(session: ThreadSession | null): SessionPhase {
  if (!session || session.status === "closed") return "disconnected";
  if (session.status === "connecting") return "connecting";
  if (session.status === "running") return "running";
  return "ready";
}
